/**
 * portalResolver — Algorithm A2 from the help-docs-panel design.
 *
 * Resolves the DOM element the chatbot FAB portals into. On `/dashboard`
 * the dashboard-redesign spec reserves an element with
 * `id="chatbot-launcher-slot"` at a fixed screen position; on any other
 * route we self-mount a slot at the exact same coordinates so the FAB
 * behaves identically everywhere.
 *
 * Invariant: after calling {@link resolvePortalTarget}, exactly one
 * element with `id="chatbot-launcher-slot"` exists in the DOM.
 *
 * Ownership contract (do NOT change):
 * - The reserved slot's fixed geometry is owned by
 *   `.kiro/specs/dashboard-redesign/`. The values below MUST match that
 *   contract byte-for-byte.
 *
 * SSR-safety:
 * - `resolvePortalTarget` throws when `document` is unavailable — it
 *   must only be called from `useEffect` / event handlers.
 * - `disposeSelfMountedSlot` is a no-op when `document` is unavailable
 *   so cleanup paths are safe on the server.
 */

/** The reserved slot id — shared contract with dashboard-redesign. */
export const CHATBOT_LAUNCHER_SLOT_ID = "chatbot-launcher-slot";

/**
 * The data-attribute we set on slots we create ourselves. The
 * dashboard's reserved slot NEVER carries this attribute — that is how
 * we tell "ours" apart from "theirs" during teardown.
 */
export const SELF_MOUNTED_ATTR = "data-self-mounted";

/**
 * Inline style contract for a self-mounted slot. Mirrors the
 * dashboard-redesign Reserved_Slot geometry exactly.
 */
const SELF_MOUNTED_SLOT_STYLE: Readonly<Record<string, string>> = {
  position: "fixed",
  bottom: "24px",
  right: "24px",
  width: "56px",
  height: "56px",
  zIndex: "40",
};

function hasDocument(): boolean {
  return typeof document !== "undefined";
}

/**
 * Resolves the DOM element the chatbot FAB should portal into.
 *
 * If `#chatbot-launcher-slot` already exists (reserved by
 * dashboard-redesign on `/dashboard`), return it. Otherwise create a
 * self-mounted div at the same fixed coordinates, append it to
 * `document.body`, and return it.
 *
 * Post-condition:
 * `document.querySelectorAll("#chatbot-launcher-slot").length === 1`
 *
 * @throws `Error` when called outside a browser environment.
 */
export function resolvePortalTarget(): HTMLElement {
  if (!hasDocument()) {
    throw new Error("portalResolver: requires a browser environment");
  }

  const existing = document.getElementById(CHATBOT_LAUNCHER_SLOT_ID);
  if (existing !== null) {
    // Dashboard reserved it — DO NOT create a duplicate.
    return existing;
  }

  const slot = document.createElement("div");
  slot.id = CHATBOT_LAUNCHER_SLOT_ID;
  slot.setAttribute(SELF_MOUNTED_ATTR, "true");

  for (const [prop, value] of Object.entries(SELF_MOUNTED_SLOT_STYLE)) {
    // `style` indexed access is `CSSStyleDeclaration`, which is
    // typed as string-indexed; cast narrowly to avoid `any`.
    (slot.style as unknown as Record<string, string>)[prop] = value;
  }

  document.body.appendChild(slot);
  return slot;
}

/**
 * If a self-mounted slot exists (one we created, marked with
 * `data-self-mounted="true"`), remove it from the DOM. Never removes
 * a reserved slot owned by dashboard-redesign.
 *
 * Idempotent — safe to call multiple times, safe to call when no
 * self-mounted slot exists, and a no-op in non-browser environments.
 */
export function disposeSelfMountedSlot(): void {
  if (!hasDocument()) {
    return;
  }

  const selector = `#${CHATBOT_LAUNCHER_SLOT_ID}[${SELF_MOUNTED_ATTR}="true"]`;
  const selfMounted = document.querySelector(selector);
  if (selfMounted !== null && selfMounted.parentNode !== null) {
    selfMounted.parentNode.removeChild(selfMounted);
  }
}
